import { useState } from 'react';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { newIdempotencyKey } from '../../lib/api';
import { useSession } from '../../lib/useSession';
import { describeApiError } from '../qrs/errors';
import { permissionsFor } from '../qrs/permissions';
import type { Lot } from '../qrs/types';
import { useOrgApi } from '../qrs/useOrgApi';
import { stripLogoUrl } from '../style/binaryApi';
import { LOCAL_PRESETS } from '../style/presets';
import { StyleEditor } from '../style/StyleEditor';
import type { QrStyle, StyleIssue } from '../style/types';
import { parseQuantity } from './lotUtils';


export function CreateLotModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (lot: Lot) => void }) {
  const client = useOrgApi();
  const { activeOrg } = useSession();
  const perms = permissionsFor(activeOrg?.role);
  const [quantity, setQuantity] = useState('500');
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [presetId, setPresetId] = useState('clasico');
  const [custom, setCustom] = useState<QrStyle | null>(null);
  const [editing, setEditing] = useState(false);
  const [issues, setIssues] = useState<StyleIssue[]>([]);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idemKey] = useState(newIdempotencyKey);

  const qty = parseQuantity(quantity);
  const style = custom ?? LOCAL_PRESETS.find((p) => p.id === presetId)?.qrStyle ?? null;
  const classic = !custom && presetId === 'clasico';

  async function submit() {
    setTouched(true);
    setError(null);
    if (qty === null) return;
    if (!classic && issues.length > 0) {
      setError('El estilo tiene avisos de lectura. Corrígelos o elige Clásico.');
      return;
    }
    setSaving(true);
    try {
      const lot = await client.post<Lot>(
        '/v1/lots',
        { quantity: qty, name: name.trim() || undefined, domain: perms.chooseDomain && domain ? domain : undefined, qrStyle: classic || !style ? undefined : stripLogoUrl(style) },
        { idempotencyKey: idemKey },
      );
      onCreated(lot);
    } catch (err) {
      setError(describeApiError(err, 'No pudimos reservar el lote.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Pedir lote de QR físicos"
      size={editing ? 'lg' : 'md'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => void submit()} loading={saving} disabled={qty === null && touched}>
            {qty ? `Reservar ${qty.toLocaleString('es-CL')} códigos` : 'Reservar códigos'}
          </Button>
        </>
      }
    >
      <Input
        label="Cantidad (1 a 5.000)"
        inputMode="numeric"
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
        onBlur={() => setTouched(true)}
        error={touched && qty === null ? 'La cantidad debe estar entre 1 y 5.000. No se reservó ningún código.' : undefined}
      />
      <Input label="Nombre del lote (opcional)" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Feria gastronómica de octubre" />
      {perms.chooseDomain && (
        <Input label="Dominio (opcional, solo operador)" value={domain} onChange={(e) => setDomain(e.target.value.toLowerCase())} placeholder="q-r.cl" hint="Vacío usa el dominio principal q-r.cl." />
      )}
      <fieldset className="space-y-2 rounded-lg border border-line p-3">
        <legend className="px-1 text-xs font-medium text-ink-muted">Estilo del lote</legend>
        <label htmlFor="lot-style" className="sr-only">
          Preset del lote
        </label>
        <select
          id="lot-style"
          className="w-full rounded-sm border border-line bg-white px-3 py-2 text-sm"
          value={custom ? 'custom' : presetId}
          onChange={(e) => {
            if (e.target.value === 'custom') {
              setEditing(true);
              setCustom(style ?? LOCAL_PRESETS[0].qrStyle);
            } else {
              setCustom(null);
              setEditing(false);
              setPresetId(e.target.value);
            }
          }}
        >
          <option value="clasico">Clásico (PDF vectorial)</option>
          <option value="suave">Suave</option>
          <option value="marca">Marca · logo del negocio al centro</option>
          <option value="menu">Menú · marco «VER MENÚ»</option>
          <option value="custom">Personalizado</option>
        </select>
        <div className="flex items-center justify-between text-xs text-ink-muted">
          <span>Todos los QR del lote comparten el estilo. Con estilo, el kit se imprime a 300 dpi.</span>
          {!editing && (
            <button type="button" className="font-medium text-green underline hover:text-ink" onClick={() => { setEditing(true); setCustom(style ?? LOCAL_PRESETS[0].qrStyle); }}>
              Personalizar estilo
            </button>
          )}
        </div>
        {editing && custom && <StyleEditor value={custom} onChange={setCustom} orgId={client.orgId} onIssues={setIssues} />}
      </fieldset>
      <p className="text-xs text-ink-muted">
        Se reservan los códigos al confirmar. Quedan en estado <b>generado</b> hasta marcar el lote como impreso.
      </p>
      {error && <Banner tone="error">{error}</Banner>}
    </Modal>
  );
}
